import React, { useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { FileText, Table, FileSpreadsheet, X, Download } from 'lucide-react';
import { API_URL } from '../../utils/api';
import { triggerDownload } from '../../utils/downloadUtils';

const ExportModal = ({ isOpen, onClose, selectedPlantIds, title = "Pflanzenliste Exportieren" }) => {
    const { authToken } = useContext(AuthContext);
    const [format, setFormat] = useState('pdf');
    const [orientation, setOrientation] = useState('portrait');
    const [groupBy, setGroupBy] = useState('none');
    const [includeThumbnails, setIncludeThumbnails] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    const handleExport = async () => {
        setIsExporting(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/plants/export/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    plant_ids: selectedPlantIds,
                    format: format,
                    orientation: orientation,
                    group_by: groupBy,
                    include_thumbnails: includeThumbnails
                })
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || err.error || 'Fehler beim Exportieren.');
            }

            const arrayBuffer = await response.arrayBuffer();
                const blob = new Blob([arrayBuffer], { type: response.headers.get('content-type') || 'application/pdf' });
            
            const ext = format === 'excel' ? 'xlsx' : format;
            const fileName = `Pflanzenliste_${new Date().toISOString().slice(0, 10)}.${ext}`;
            
            triggerDownload(blob, fileName);
            onClose();
        } catch (e) {
            setError(e.message);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-[#1a1a1a] w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700 animate-fadeIn" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Download className="w-5 h-5 text-plantlix-500" />
                        {title}
                    </h3>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                <div className="p-6 space-y-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                            Dateiformat
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                            <button
                                onClick={() => setFormat('pdf')}
                                className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${format === 'pdf' ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-red-200'}`}
                            >
                                <FileText className="w-6 h-6" />
                                <span className="text-sm font-medium">PDF</span>
                            </button>
                            <button
                                onClick={() => setFormat('excel')}
                                className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${format === 'excel' ? 'border-plantlix-500 bg-plantlix-50 dark:bg-plantlix-900/20 text-plantlix-700 dark:text-plantlix-400' : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-plantlix-200'}`}
                            >
                                <Table className="w-6 h-6" />
                                <span className="text-sm font-medium">Excel</span>
                            </button>
                            <button
                                onClick={() => setFormat('odt')}
                                className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${format === 'odt' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-blue-200'}`}
                            >
                                <FileSpreadsheet className="w-6 h-6" />
                                <span className="text-sm font-medium">ODT</span>
                            </button>
                        </div>
                    </div>

                    {format === 'pdf' && (
                        <div className="animate-fadeIn">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                Ausrichtung
                            </label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input 
                                        type="radio" 
                                        checked={orientation === 'portrait'} 
                                        onChange={() => setOrientation('portrait')} 
                                        className="w-4 h-4 text-plantlix-500 focus:ring-plantlix-500"
                                    />
                                    <span className="text-gray-700 dark:text-gray-300">Hochformat</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input 
                                        type="radio" 
                                        checked={orientation === 'landscape'} 
                                        onChange={() => setOrientation('landscape')}
                                        className="w-4 h-4 text-plantlix-500 focus:ring-plantlix-500" 
                                    />
                                    <span className="text-gray-700 dark:text-gray-300">Querformat</span>
                                </label>
                            </div>
                        </div>
                    )}

                    <div className="animate-fadeIn">
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                            Gruppieren nach
                        </label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                    type="radio" 
                                    checked={groupBy === 'none'} 
                                    onChange={() => setGroupBy('none')} 
                                    className="w-4 h-4 text-plantlix-500 focus:ring-plantlix-500"
                                />
                                <span className="text-gray-700 dark:text-gray-300">Ohne</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                    type="radio" 
                                    checked={groupBy === 'category'} 
                                    onChange={() => setGroupBy('category')}
                                    className="w-4 h-4 text-plantlix-500 focus:ring-plantlix-500" 
                                />
                                <span className="text-gray-700 dark:text-gray-300">Kategorie</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                    type="radio" 
                                    checked={groupBy === 'family'} 
                                    onChange={() => setGroupBy('family')}
                                    className="w-4 h-4 text-plantlix-500 focus:ring-plantlix-500" 
                                />
                                <span className="text-gray-700 dark:text-gray-300">Familie</span>
                            </label>
                        </div>
                    </div>

                    <div className="animate-fadeIn mt-2">
                        <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 transition-colors">
                            <input 
                                type="checkbox" 
                                checked={includeThumbnails} 
                                onChange={(e) => setIncludeThumbnails(e.target.checked)} 
                                className="w-5 h-5 text-plantlix-500 focus:ring-plantlix-500 rounded cursor-pointer"
                            />
                            <div className="flex flex-col">
                                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Mit Bildern exportieren</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">Thumbnails in den Export aufnehmen</span>
                            </div>
                        </label>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800">
                        <p className="text-sm text-blue-800 dark:text-blue-300">
                            <strong>{selectedPlantIds.length}</strong> Pflanzen werden exportiert. {includeThumbnails ? 'Bilder und ' : ''}wichtigste Merkmale sind enthalten.
                        </p>
                    </div>

                    {error && (
                        <div className="text-red-500 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-sm border border-red-200 dark:border-red-800">
                            {error}
                        </div>
                    )}
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-3">
                    <button 
                        onClick={onClose}
                        disabled={isExporting}
                        className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                        Abbrechen
                    </button>
                    <button 
                        onClick={handleExport}
                        disabled={isExporting || selectedPlantIds.length === 0}
                        className="flex items-center gap-2 px-6 py-2 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-lg font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-70 disabled:hover:shadow-md"
                    >
                        {isExporting ? (
                            <>
                                <span className="animate-spin text-xl">⏳</span>
                                Exportiert...
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4" />
                                Herunterladen
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportModal;
